import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { refreshBlobUrls, useFetchData, useLoadImageWithAuth } from './apiHooks';
import { API_ENDPOINTS } from 'constants/index.js';
import parse from 'html-react-parser';
import DOMPurify from "dompurify";
import TextWithLineBreaks from 'components/Editor/TextWithLineBreaks';
import CalendarEvent from 'components/modules/email/CalendarEvent';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { useNavigate } from 'react-router-dom';
import { OBJ } from 'constants';

export function useFetchEmailOnSelection(selectedItem, refetch, email) {
  useEffect(() => {
    if (selectedItem && email && (!email.isLoading && email.id !== selectedItem.id)) {
        console.log("refetch in useFetchEmailOnSelection", selectedItem, email)
      refetch();
    }
  }, [selectedItem, refetch, email]);
}

export function useMarkEmailAsRead(email, handleEmailChange) {
    const hasMarkedRead = useRef(false);
    useEffect(() => {
        if (email && !hasMarkedRead.current) {
            if(!email.is_read){
                handleEmailChange('is_read', true);
            }
            hasMarkedRead.current = true;
        }
    }, [email, handleEmailChange]);
}

export function useFetchRelatedEmails(email, emailToRespond, fetchEmailToRespond) {
  useEffect(() => {
    if (email?.replied_to_email_id && !emailToRespond) {
      fetchEmailToRespond();
    }
  }, [email, emailToRespond, fetchEmailToRespond]);
}

export const useHtmlEmail = (email) => {
    return useMemo(() => {
        if (!email?.body) return <div></div>;
        if (email.content_type === 'text/plain' && typeof email.body === 'string') 
            return <TextWithLineBreaks text={email.body}/>
        if (email.content_type === 'text/calendar') {
            return <CalendarEvent icsData={email.body} />
        }    
        return parse(DOMPurify.sanitize(email?.body, {
            ALLOWED_URI_REGEXP: /^(?:(?:(?:https?|blob|data):)|(?:[^:/?#]*(?:[/?#]|$)))/i,
            ADD_ATTR: ['data-backend-id', 'filename'],
        }))   
    }, [email?.body, email?.content_type]);
};

export const replaceSignatureInBody = (body, oldSignatureContent, newSignatureContent) => {
    const currentBody = body || '';
    const oldContent = oldSignatureContent || '';
    const newContent = newSignatureContent || '';

    if (!oldContent) {
        return currentBody.trim() ? currentBody : newContent;
    }

    if (currentBody === oldContent || currentBody.trim() === oldContent.trim()) {
        return newContent;
    }

    if (currentBody.endsWith(oldContent)) {
        return currentBody.slice(0, -oldContent.length) + newContent;
    }

    return currentBody;
};

export const useNewEmailDraft = (accountSettingsId) => {
    const navigate = useNavigate(); 
    const [standardSignature, setStandardSignature] = useState({ content: '' });
    const { saveData: createEmail, saveError: errorCreateEmail, makeApiCall } = useFetchData(API_ENDPOINTS.EMAILS.LIST, null, false);

    const fetchSignature = async (settingsId = accountSettingsId) => {
        const accountSettingEndpoint = settingsId
            ? API_ENDPOINTS.ACCOUNT_SETTINGS.ITEM(settingsId)
            : API_ENDPOINTS.ACCOUNT_SETTINGS.DEFAULT;
        const {data: accountSetting} = await makeApiCall(accountSettingEndpoint, {}, null, null, () => {}, 'GET');
        if (!accountSetting?.signature_id) {
            setStandardSignature({ content: '' });
            return { content: '' };
        }
    
        const {data: signature} = await makeApiCall(API_ENDPOINTS.SIGNATURES.ITEM(accountSetting.signature_id), {}, null, null, () => {}, 'GET') || { content: '' };
        setStandardSignature(signature);
        return signature;
    };

    useEffect(() => {
        fetchSignature(accountSettingsId);
      }, [accountSettingsId]);

    const createDraft = async (contactId = null, recipients, settingsId = accountSettingsId) => {
        let body = standardSignature.content || '';
        if (!body) {
            const fresh = await fetchSignature(settingsId);
            body = fresh.content || '';
        }

        return {
            body,
            status: 'draft',
            is_read: true,
            contact_id: contactId || undefined,
            recipients: recipients
        };
    };

    const createNewEmailDraft = async (newLine, contactId, recipients) => {
        const draft = await createDraft(contactId, recipients);
        Object.assign(newLine, draft);
        return newLine
    };

    const createNewEmail = async (contactId, recipients) => {
        const newEmailDraft = await createNewEmailDraft({ id: -1 }, contactId, recipients)
        return await createEmail({items: [newEmailDraft]})
    };

    const createAndOpenEmail = async (contactId, emailAddress) => {
        const newEmail = await createNewEmail(contactId, [emailAddress]);
        console.log(newEmail)
        navigate(`/admin/${OBJ.EMAILS}/${newEmail[0].id}`, { replace: false });
    }

    return { standardSignature, fetchSignature, createDraft, createNewEmailDraft, createNewEmail, errorCreateEmail, createAndOpenEmail };
};

export function useUpdateImages(email, divRef) {
    const loadImageWithAuth = useLoadImageWithAuth();

    useEffect(() => {
        if (email?.body && divRef.current) {
            updateImage();  
        }
    }, [email]);

    async function updateImage(){
        await refreshBlobUrls(divRef.current, loadImageWithAuth);
    }
}

const EmailContentRenderer = ({ email }) => { 
    const divRef = useRef(null);
    const htmlEmail = useHtmlEmail(email);
    useUpdateImages(email, divRef);

    return (
        <ErrorBoundary fallback={<div>An error occurred in the HTML Email</div>}>
            <div ref={divRef} className="table-responsive">
                {htmlEmail}
            </div>
        </ErrorBoundary>
    );
};

export default EmailContentRenderer;